import { Link } from 'react-router-dom';
import { useInView } from '../../hooks/useInView';
import FAQ from '../home/components/FAQ';
import FinalCTA from '../home/components/FinalCTA';
import ServiceAreas from '../home/components/ServiceAreas';
import Testimonials from '../home/components/Testimonials';
import AIAnswerBlock from '../home/components/AIAnswerBlock';
import MidPageCTA from '../home/components/MidPageCTA';
import TrustBar from '../home/components/TrustBar';

const values = [
  { icon: 'ri-heart-3-fill', title: 'Purpose-Driven', desc: 'Every brand has a story worth telling with intention and impact. We exist to bring that story to life at the highest possible broadcast standard.' },
  { icon: 'ri-award-fill', title: 'Uncompromising Quality', desc: 'From pre-production to final delivery, we hold every frame to the highest cinematic standard — no exceptions, no compromises, no shortcuts.' },
  { icon: 'ri-handshake-fill', title: 'True Partnership', desc: 'We don\'t just deliver content. We invest deeply in your success as a genuine creative and strategic partner from day one.' },
  { icon: 'ri-lightbulb-flash-fill', title: 'Continuous Innovation', desc: 'We continuously evolve our craft, tools, and production approach to keep our clients at the forefront of visual media and storytelling.' },
];

const aboutQAs = [
  { q: 'Who is Immanuel Production Group?', a: 'Immanuel Production Group is a professional broadcast-grade media production company based in Oviedo, Central Florida. Focused on delivering cinematic video production and photography services, their mission is to help brands communicate powerfully through world-class visual storytelling.' },
  { q: 'What is the company\'s background and experience?', a: 'Founded in 2005, Immanuel Production Group brings 20+ years of professional broadcast production experience across multiple industries — including corporate, nonprofit, government, retail, and entertainment. They serve clients from Oviedo, Orlando, Tampa, and Miami to nationwide markets.' },
  { q: 'What is their vision for cinematic storytelling?', a: 'They believe visual storytelling is the most powerful tool available to modern brands. Their vision is to make broadcast-grade production accessible to every organization with a mission worth sharing — and to execute it at the highest standard without exception.' },
];

function AboutHero() {
  return (
    <section className="relative min-h-[85vh] flex items-end justify-start overflow-hidden bg-[#020202]">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f7317f2cd2b9c18df70cc45cdcf33901.jpg"
          alt="Immanuel Production Group - 20+ years of broadcast excellence"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020202] via-[#020202]/55 to-[#020202]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020202]/80 to-transparent" />
        <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none" />
      </div>
      <div className="relative z-10 max-w-7xl w-full mx-auto px-8 lg:px-20 pb-24 pt-52">
        <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Our Story</p>
        <h1 className="font-serif font-bold text-white leading-[0.95] mb-6" style={{ fontSize: 'clamp(3.2rem, 7vw, 7rem)', letterSpacing: '-0.03em' }}>
          About Immanuel<br />
          <em className="not-italic text-gold">Production Group</em>
        </h1>
        <p className="font-sans text-lg text-white/50 font-light leading-relaxed max-w-xl">
          Two decades of broadcast-level cinematic production in Central Florida. One unwavering mission — helping brands communicate powerfully through visual excellence.
        </p>
        <div className="flex items-center gap-5 mt-10">
          {[['20+','Years'],['500+','Productions'],['2','Languages'],['100%','Excellence']].map(([n,l]) => (
            <div key={l} className="text-center hidden md:block">
              <p className="font-serif text-2xl font-bold text-gold leading-none">{n}</p>
              <p className="font-sans text-[0.55rem] tracking-[0.2em] uppercase text-white/30 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StoryMission() {
  const { ref, inView } = useInView();
  return (
    <section ref={ref as React.RefObject<HTMLDivElement>} className="py-36 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-8 lg:px-20">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-20 items-center transition-all duration-900 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Image side */}
          <div className="relative">
            <div className="relative overflow-hidden" style={{ height: '580px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20cinematographer%20looking%20through%20cinema%20camera%20viewfinder%20dark%20broadcast%20studio%20atmospheric%20warm%20golden%20rim%20light%20moody%20atmospheric%20deep%20blacks%20high%20contrast%20cinematic%20film%20production%20authentic%20behind%20scenes%20documentary%20professional%20media&width=700&height=800&seq=ab3_story&orientation=portrait"
                alt="Immanuel Production Group 20+ year production story"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/70 via-transparent to-transparent" />
            </div>
            {/* Credential card overlap */}
            <div className="absolute -bottom-6 -right-6 bg-[#0c0c0c] border border-gold/20 p-7 w-52">
              <p className="font-serif text-5xl font-bold text-gold leading-none">20+</p>
              <div className="w-8 h-px bg-gold/30 my-3" />
              <p className="font-sans text-[0.58rem] tracking-[0.2em] uppercase text-white/38">Years of Broadcast Excellence</p>
            </div>
          </div>

          {/* Content side */}
          <div>
            <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Our Story &amp; Mission</p>
            <h2 className="font-serif font-bold text-white leading-tight mb-7" style={{ fontSize: 'clamp(2.5rem, 4vw, 4rem)', letterSpacing: '-0.02em' }}>
              All Things Media —<br />
              <em className="not-italic text-gold">Concept to Completion</em>
            </h2>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-px bg-gold" />
              <div className="w-4 h-px bg-gold/40" />
            </div>
            <div className="space-y-5 font-sans text-sm text-white/48 leading-[2]">
              <p>Immanuel Production Group was founded with a powerful belief: every brand, organization, and individual deserves the opportunity to tell their story with professional broadcast-grade visual excellence.</p>
              <p>Over 20 years delivering award-level productions across Central Florida — from Oviedo and Orlando to Tampa, Miami, and beyond — our team has become the trusted creative partner for corporations, nonprofits, government organizations, and visionary entrepreneurs.</p>
              <p>We are a bilingual production team built on technical mastery, creative integrity, and an unwavering commitment to bringing your vision to life at the highest possible standard. From broadcast commercials to cinematic brand films, we handle every stage — concept to final delivery.</p>
              <p>Our clients don't just receive a video. They receive a strategic broadcast-quality creative asset built to generate authority, engagement, and lasting impact.</p>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-6">
              {[['20+','Years'],['500+','Projects'],['2','Languages']].map(([n, l]) => (
                <div key={l} className="border-l-2 border-gold/25 pl-4">
                  <p className="font-serif text-3xl font-bold text-gold leading-none">{n}</p>
                  <p className="font-sans text-[0.58rem] text-white/32 tracking-[0.22em] uppercase mt-2">{l}</p>
                </div>
              ))}
            </div>

            <Link to="/contact" className="whitespace-nowrap mt-10 inline-flex items-center gap-3 font-sans text-[0.68rem] tracking-[0.22em] uppercase px-9 py-4 bg-gold text-[#020202] hover:bg-white transition-colors duration-300 cursor-pointer group font-semibold">
              Work With Us
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function OurValues() {
  const { ref, inView } = useInView();
  return (
    <section ref={ref as React.RefObject<HTMLDivElement>} className="py-36 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0c0c0c 0%, #0f0f0f 100%)' }}>
      <div className="max-w-7xl mx-auto px-8 lg:px-20">
        <div className={`text-center mb-20 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">What We Stand For</p>
          <h2 className="font-serif font-bold text-white" style={{ fontSize: 'clamp(2.8rem, 5vw, 5rem)', letterSpacing: '-0.025em' }}>
            Our <em className="not-italic text-gold">Values</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <div
              key={v.title}
              className={`p-10 lg:p-12 border-t border-white/5 hover:bg-white/[0.018] transition-all duration-500 cursor-default group ${i < values.length - 1 ? 'border-r border-white/5' : ''} ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center border border-gold/20 group-hover:border-gold/55 mb-8 transition-colors duration-300">
                <i className={`${v.icon} text-2xl text-gold`} />
              </div>
              <h3 className="font-serif text-xl font-semibold text-white mb-4">{v.title}</h3>
              <div className="w-8 h-px bg-gold/25 mb-5 group-hover:bg-gold transition-colors duration-300" />
              <p className="font-sans text-sm text-white/38 leading-relaxed group-hover:text-white/60 transition-colors duration-300">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowWeWork() {
  const steps = [
    { num: '01', title: 'Listen & Understand', desc: 'We begin every broadcast production project by deeply understanding your brand, audience, and business goals before any creative direction is established.' },
    { num: '02', title: 'Strategize & Create', desc: 'Our team develops a production strategy that aligns cinematic storytelling with measurable business outcomes — purpose behind every single frame.' },
    { num: '03', title: 'Execute with Precision', desc: 'Broadcast-grade crew, professional cinema equipment, and masterful direction bring the vision to life with the precision of 20+ years in the industry.' },
    { num: '04', title: 'Refine & Deliver', desc: 'Post-production excellence, then delivery optimized for every platform where your content needs to perform, engage audiences, and convert viewers.' },
  ];
  const { ref, inView } = useInView();

  return (
    <section ref={ref as React.RefObject<HTMLDivElement>} className="py-36 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-8 lg:px-20">
        <div className={`text-center mb-20 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Our Process</p>
          <h2 className="font-serif font-bold text-white" style={{ fontSize: 'clamp(2.8rem, 5vw, 5rem)', letterSpacing: '-0.025em' }}>How We Work</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div
              key={s.num}
              className={`p-9 border border-white/5 hover:border-gold/20 transition-all duration-400 group cursor-default ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="font-serif font-bold text-gold/8 group-hover:text-gold/20 transition-colors duration-500 mb-5 leading-none" style={{ fontSize: '4.5rem' }}>{s.num}</div>
              <div className="w-8 h-px bg-gold/25 mb-5 group-hover:bg-gold transition-colors duration-300" />
              <h3 className="font-sans text-sm font-semibold text-white mb-3">{s.title}</h3>
              <p className="font-sans text-[0.75rem] text-white/38 leading-relaxed group-hover:text-white/60 transition-colors duration-300">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <main>
      <AboutHero />
      <TrustBar />
      <StoryMission />
      <MidPageCTA />
      <AIAnswerBlock heading="About Immanuel Production Group" qas={aboutQAs} />
      <OurValues />
      <HowWeWork />
      <ServiceAreas />
      <Testimonials />
      <FAQ />
      <FinalCTA title="Ready to Work Together?" subtitle="Let's create broadcast-level content your audience will never forget." />
    </main>
  );
}
